import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LEGAL_UPDATED_LABEL, LEGAL_VERSION } from '@/legal/version';

export default function ConsumerHealthPrivacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-paper py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          className="text-forest-ink hover:text-forest-shadow hover:bg-keylime-wash mb-6"
          onClick={() => navigate('/')}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="text-center mb-8 sm:mb-12 px-2">
          <Badge className="mb-3 sm:mb-4 bg-keylime-wash text-charcoal border-border-mist px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-semibold">
            <Shield className="h-3 w-3 mr-1.5 inline" />
            Consumer Health Data
          </Badge>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-forest-ink mb-3 sm:mb-4 break-words">
            Consumer Health Data Privacy Policy
          </h1>
          <p className="text-sm sm:text-base text-charcoal max-w-3xl mx-auto leading-relaxed">
            Version {LEGAL_VERSION} · Last updated {LEGAL_UPDATED_LABEL}
          </p>
        </div>

        <Card className="bg-keylime-wash border border-border-mist">
          <CardContent className="p-4 sm:p-6 md:p-8 space-y-6 text-charcoal text-sm sm:text-base leading-relaxed">
            <p>
              This policy describes how PraesidioCare Private Limited (“ByOnco”) collects, uses, and
              shares consumer health data. It is provided to meet consumer health privacy laws in
              Washington (My Health My Data Act), Nevada, and Connecticut, and applies in addition to
              our general Privacy Policy.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">1. What is consumer health data</h2>
            <p>
              Consumer health data includes information that identifies or can reasonably be linked to
              you and relates to your health condition, diagnosis, treatment, medications, symptoms,
              health-care services sought, or inferences about your health. For ByOnco this includes
              cancer history you enter, uploaded reports, chat and voice transcripts, and related
              account records.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">2. What we collect</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Account identifiers (email, phone) needed to provide the service</li>
              <li>Health information you type, speak, or upload</li>
              <li>Subscription and credit-usage metadata (not a medical record)</li>
              <li>Safety-event flags (for example, that an emergency classifier fired), without storing the message body in application logs</li>
            </ul>

            <h2 className="text-xl font-semibold text-forest-ink">3. How we use it</h2>
            <p>
              We use consumer health data only to provide the ByOnco assistant, document storage,
              safety routing, billing for the plan you bought, and legally required records. We do not
              sell consumer health data. We do not use it for targeted advertising.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">4. Sharing</h2>
            <p>
              We share consumer health data with processors who host the product (cloud database,
              object storage, large-language-model providers, speech providers, and payment
              processors) under contracts that limit use to providing our service. We share with
              authorities when required by law. We do not share consumer health data with data
              brokers.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">5. Your rights</h2>
            <p>
              Residents of Washington, Nevada, and Connecticut may request access, deletion, or a
              list of third parties with which consumer health data was shared, and may withdraw
              consent for collection of consumer health data, subject to records we must keep for
              law, security, or billing disputes. Email{' '}
              <a className="underline" href="mailto:care@getbyonco.com">care@getbyonco.com</a>
              {' '}with the subject “Consumer health data request”.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">6. Consent</h2>
            <p>
              We collect consumer health data only after you give separate consent at registration or
              OTP login. You may withdraw consent by requesting account deletion. Withdrawal does not
              affect processing already completed.
            </p>

            <h2 className="text-xl font-semibold text-forest-ink">7. Contact</h2>
            <p>
              PraesidioCare Private Limited ·{' '}
              <a className="underline" href="mailto:care@getbyonco.com">care@getbyonco.com</a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
